import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ConfirmModal from "./ConfirmModal";
import SuccessModal from "./SuccessModal";
import { Eye, EyeOff } from "lucide-react";
import { updatePassword } from "../api/user";
import { ToastContainer, toast } from "react-toastify";
import Loader from "../components/Loader";

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ChangePasswordModal = ({ isOpen, onClose }: ChangePasswordModalProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [confirmshowPassword, setConfirmShowPassword] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [errors, setErrors] = useState<{
    oldPassword?: string;
    newPassword?: string;
  }>({});
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const resetFields = () => {
    setOldPassword("");
    setNewPassword("");
    setErrors({});
    setShowConfirm(false);
  };

  const validate = () => {
    const newErrors: { oldPassword?: string; newPassword?: string } = {};
    if (!oldPassword) newErrors.oldPassword = "Old password is required";
    if (!newPassword) newErrors.newPassword = "New password is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOpenConfirm = () => {
    if (validate()) {
      setShowConfirm(true);
    }
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);
      await updatePassword(oldPassword, newPassword);
      setShowConfirm(false);
      setShowSuccess(true);
      resetFields();
      // Do not close parent here; success modal will handle close via its onClose
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to update password.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    resetFields();
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {loading && <Loader />}
          <motion.div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 mx-2"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <ToastContainer position="top-right" autoClose={3000} />
            {loading && <Loader />}
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Change Password
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-600">
                  Old Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    className={`mt-1 w-full p-2 pr-10 border rounded-xl focus:ring-2 focus:ring-blue-500 ${
                      errors.oldPassword ? "border-red-500" : ""
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-3 flex items-center text-gray-500"
                  >
                    {showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
                  </button>
                </div>
                {errors.oldPassword && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.oldPassword}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={confirmshowPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={`mt-1 w-full p-2 pr-10 border rounded-xl focus:ring-2 focus:ring-blue-500 ${
                      errors.newPassword ? "border-red-500" : ""
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setConfirmShowPassword(!confirmshowPassword)}
                    className="absolute inset-y-0 right-3 flex items-center text-gray-500"
                  >
                    {confirmshowPassword ? (
                      <Eye size={18} />
                    ) : (
                      <EyeOff size={18} />
                    )}
                  </button>
                </div>
                {errors.newPassword && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.newPassword}
                  </p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={handleCancel}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleOpenConfirm}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition"
                disabled={loading}
              >
                {loading ? "Processing..." : "Change Password"}
              </button>
            </div>
            {/* Confirm Modal */}
            <ConfirmModal
              isOpen={showConfirm}
              title="Confirm Password Change"
              message="Are you sure you want to change your password?"
              confirmText="Yes, Change"
              cancelText="Cancel"
              onConfirm={handleConfirm}
              onCancel={() => setShowConfirm(false)}
              loading={loading}
            />
            <SuccessModal
              isOpen={showSuccess}
              title="Password Changed!"
              message="Your password has been updated successfully."
              buttonText="Close"
              onClose={() => {
                setShowSuccess(false);
                onClose();
              }}
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ChangePasswordModal;
